// Scroll reveal for the marketing site (Delight System §12). Styles live in
// frontend/styles/delight.css; nothing is hidden unless html.nb-js is set (an
// inline script in _head.erb does that before first paint).
//
//   [data-reveal]          the block rises in when it enters the viewport;
//                          data-reveal-delay="120" offsets it in ms
//   [data-reveal-stagger]  its children rise in one after another, 80ms apart
//   [data-reveal-words]    a headline whose words rise in 40ms apart, then its
//                          .nb-underline draws on
//
// Each element reveals once and never replays on scroll-up.

const STAGGER_MS = 80

const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches

// Wrap each word of a headline in .nb-wd, keeping inline markup (spans, <br>)
// intact so translated, highlighted headings survive. Text without spaces
// (Chinese) stays one unit per text node, which is fine.
const splitWords = (root) => {
  let i = 0
  const walk = (node) => {
    ;[...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const parts = child.textContent.split(/(\s+)/)
        if (parts.every((p) => !p.trim())) return
        const frag = document.createDocumentFragment()
        parts.forEach((part) => {
          if (!part) return
          if (!part.trim()) return frag.append(part)
          const word = document.createElement("span")
          word.className = "nb-wd"
          word.style.setProperty("--i", i++)
          word.textContent = part
          frag.append(word)
        })
        child.replaceWith(frag)
      } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== "svg" && child.tagName !== "BR") {
        walk(child)
      }
    })
  }
  walk(root)
  root.style.setProperty("--nb-words", i)
  root.classList.add("is-split")
}

// Once revealed, drop the stagger delays so hover lifts respond instantly.
const settle = (el, ms) => {
  setTimeout(() => {
    el.style.transitionDelay = ""
    ;[...el.children].forEach((child) => (child.style.transitionDelay = ""))
  }, ms)
}

const reveal = (el) => {
  el.classList.add("is-in")
  if (el.hasAttribute("data-reveal-stagger")) settle(el, el.children.length * STAGGER_MS + 700)
  else settle(el, 900)
}

const init = () => {
  document.querySelectorAll("[data-reveal-words]").forEach(splitWords)

  document.querySelectorAll("[data-reveal-stagger]").forEach((group) => {
    ;[...group.children].forEach((child, i) => {
      child.style.transitionDelay = `${i * STAGGER_MS}ms`
    })
  })
  document.querySelectorAll("[data-reveal][data-reveal-delay]").forEach((el) => {
    el.style.setProperty("--nb-delay", `${el.dataset.revealDelay}ms`)
  })

  const targets = document.querySelectorAll("[data-reveal], [data-reveal-stagger], [data-reveal-words]")
  if (reduced() || !("IntersectionObserver" in window)) {
    targets.forEach((el) => el.classList.add("is-in"))
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        reveal(entry.target)
        observer.unobserve(entry.target)
      })
    },
    // Reveal a little before the element is fully on screen, so a fast scroll
    // never lands on an empty section.
    { rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
  )
  targets.forEach((el) => observer.observe(el))

  // Safety net: an IntersectionObserver only reports threshold crossings, so
  // an anchor jump or a hard fling can carry a short element from below the
  // viewport to above it without ever reporting it. On scroll, anything whose
  // top has passed the reveal line is revealed outright.
  const pending = new Set(targets)
  let queued = false
  const sweep = () => {
    queued = false
    const line = window.innerHeight * 0.9
    pending.forEach((el) => {
      if (el.classList.contains("is-in")) return pending.delete(el)
      if (el.getBoundingClientRect().top < line) {
        reveal(el)
        observer.unobserve(el)
        pending.delete(el)
      }
    })
    if (pending.size === 0) window.removeEventListener("scroll", onScroll)
  }
  const onScroll = () => {
    if (queued) return
    queued = true
    requestAnimationFrame(sweep)
  }
  window.addEventListener("scroll", onScroll, { passive: true })
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init)
else init()
